import { useMutation } from '@tanstack/react-query';
import { queryClient } from 'Context/QueryClientContext';
import ObrasApi from 'Data/Obras.api';
import toastHandler from 'Utils/toastHandler';
import { FILES_QUERY_KEY } from 'Data/keys/QueryKeys';
import { IUseQueryFilesReturn } from 'Data/interfaces/ListagemArquivos/IListFiles';
import { useDispatch } from 'react-redux';
import { ViewerActions } from 'Store/Viewer/Viewer.actions';
import { IVerifyAutodeskProgress } from 'Data/interfaces/ListagemArquivos/IVerifyAutodeskProgress';
import { loadError } from 'Utils/Messages';
import { IRenderingSignalR } from 'Data/interfaces/ListagemArquivos/IListFilesResponse';
import { IAutodeskViewer } from 'Data/interfaces/Viewer/IAutodeskViewer';

export const useQueryVerifyAutodeskProfressFiles = () => {
  const dispatch = useDispatch();

  const { mutate, mutateAsync, isPending } = useMutation({
    mutationFn: (variables: IVerifyAutodeskProgress) => {
      return ObrasApi.verifyFileRenderProgress(
        variables.fileApiId,
        variables.constructionSiteId,
      );
    },
    onSuccess: async ({ data }, variables) => {
      await queryClient.cancelQueries({
        queryKey: [FILES_QUERY_KEY, Number(variables.constructionSiteId), variables.folderId],
      });

      let etapa = 0;
      let progress =
        data.data.progress && data.data.progress.indexOf('%') !== -1
          ? Number(data.data.progress.split('%')[0])
          : 0;

      switch (data.data.status) {
        case 'inprogress':
          etapa = 3;
          break;
        case 'success':
          etapa = 4;
          progress = 100;
          break;
        case 'notstarted':
          etapa = 1;
          break;
        case 'failed':
          etapa = 5;
          progress = 100;
          break;
      }

      const body: IRenderingSignalR = {
        etapa,
        fileIdentifier: variables.fileApiId || '',
        progress,
        urn: data.data.urn,
      };

      queryClient.setQueryData<IUseQueryFilesReturn>(
        [FILES_QUERY_KEY, Number(variables.constructionSiteId), variables.folderId],
        (old) => {
          if (old) {
            const index = old.ListFiles.findIndex((file) => {
              return file.Identifier.ApiId === body.fileIdentifier;
            });
            if (index !== -1) {
              if (!old.ListFiles[index].RenderingSignalR) {
                old.ListFiles[index].RenderingSignalR = {
                  ...body,
                  etapa: 1,
                  progress: 0,
                };
              }
              if (
                body.etapa > old.ListFiles[index].RenderingSignalR.etapa ||
                (body.etapa === old.ListFiles[index].RenderingSignalR.etapa &&
                  body.progress > old.ListFiles[index].RenderingSignalR.progress)
              ) {
                old.ListFiles[index].RenderingSignalR = { ...body };
                old.ListFiles[index].RenderingStatus = 2;
              }
              if (body.etapa === 4) {
                old.ListFiles[index].RenderingSignalR = {
                  ...old.ListFiles[index].RenderingSignalR,
                  etapa: 0,
                  progress: 0,
                };
                old.ListFiles[index].RenderingStatus = 1;
              }
              if (body.etapa === 5) {
                old.ListFiles[index].RenderingSignalR = {
                  ...body,
                  progress: 0,
                };
                old.ListFiles[index].RenderingStatus = 3;
              }
            }
            return { ...old };
          }
          return old;
        },
      );
      dispatch(ViewerActions.updateStatusFileViewerAutodesk(body));
    },
    onError: async () => {
      const errorMsg = loadError('informações da renderização de um arquivo');
      toastHandler.showError(errorMsg);
    },
  });

  return {
    verifyAutodeskProfressFiles: mutate,
    verifyAutodeskProfressFilesAsync: mutateAsync,
    isLoading: isPending,
  };
};

export const setFileStatusAutodeskRendering = async (
  constructionSiteId: number,
  folderId: string,
  data: IRenderingSignalR,
) => {
  await queryClient.cancelQueries({
    queryKey: [FILES_QUERY_KEY, Number(constructionSiteId), folderId],
  });

  queryClient.setQueryData<IUseQueryFilesReturn>(
    [FILES_QUERY_KEY, Number(constructionSiteId), folderId],
    (old) => {
      if (old) {
        return {
          ...old,
          ListFiles: old.ListFiles.map((file) => {
            if (file.Identifier.ApiId === data.fileIdentifier) {
              if (!file.RenderingSignalR) {
                return {
                  ...file,
                  RenderingSignalR: {
                    ...data,
                    etapa: 1,
                    progress: 0,
                  },
                };
              }
              if (
                data.etapa > file.RenderingSignalR.etapa ||
                (data.etapa === file.RenderingSignalR.etapa &&
                  data.progress > file.RenderingSignalR.progress)
              ) {
                return {
                  ...file,
                  RenderingSignalR: { ...data },
                  RenderingStatus: 2,
                };
              }
              if (data.etapa === 4) {
                return {
                  ...file,
                  RenderingSignalR: {
                    ...file.RenderingSignalR,
                    etapa: 0,
                    progress: 0,
                  },
                  RenderingStatus: 1,
                };
              }
              if (data.etapa === 5) {
                return {
                  ...file,
                  RenderingSignalR: {
                    ...data,
                    progress: 0,
                  },
                  RenderingStatus: 3,
                };
              }
            }
            return file;
          }),
        };
      }
      return old;
    },
  );
};

export const resetFileStatusRendering = async (
  data: IAutodeskViewer,
) => {
  await queryClient.cancelQueries({
    queryKey: [FILES_QUERY_KEY, Number(data.constructionSiteId), data.folderId],
  });

  queryClient.setQueryData<IUseQueryFilesReturn>(
    [FILES_QUERY_KEY, Number(data.constructionSiteId), data.folderId],
    (old) => {
      if (old) {
        return {
          ...old,
          ListFiles: old.ListFiles.map((file) => {
            if (file.Identifier.ApiId === data.fileApiId) {
              return {
                ...file,
                RenderingSignalR: {
                  etapa: 0,
                  progress: 0,
                  fileIdentifier: data.fileApiId,
                },
              };
            }
            return file;
          }),
        };
      }
      return old;
    },
  );
};
